/**
 * Date Utilities
 * 
 * Centralized date handling for the Remidy application.
 * All dates from the API should be in YYYY-MM-DD format (date-only strings).
 * This module provides consistent parsing and formatting across the app.
 */

/**
 * Parse a date value into a Date object at local midnight.
 * Handles:
 * - YYYY-MM-DD strings (preferred API format)
 * - ISO strings like "2026-04-24T00:00:00.000Z"
 * - Date objects
 * 
 * @param {string|Date|null} value - The date value to parse
 * @returns {Date|null} - Date at local midnight, or null if invalid
 */
export function parseDate(value) {
  if (!value) return null;
  
  let year, month, day;
  
  if (value instanceof Date) {
    // Use UTC values from Date object to avoid timezone shift
    // (in case it was created from an ISO string)
    year = value.getUTCFullYear();
    month = value.getUTCMonth();
    day = value.getUTCDate();
  } else if (typeof value === 'string') {
    // Extract YYYY-MM-DD from string
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    year = parseInt(match[1], 10);
    month = parseInt(match[2], 10) - 1; // JS months are 0-indexed
    day = parseInt(match[3], 10);
  } else {
    return null;
  }
  
  // Create date at local midnight
  const date = new Date(year, month, day, 0, 0, 0, 0);
  
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Format a Date object to YYYY-MM-DD string.
 * Uses local date components to avoid timezone issues.
 * 
 * @param {Date|null} date - The date to format
 * @returns {string|null} - YYYY-MM-DD string, or null if invalid
 */
export function toDateString(date) {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return null;
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}

/**
 * Format a date for display (e.g., "Jan 15, 2026")
 * 
 * @param {string|Date|null} value - Date to format
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string|null} - Formatted date string, or null if invalid
 */
export function formatDate(value, options = {}) {
  const date = parseDate(value);
  if (!date) return null;
  
  const defaultOptions = { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric' 
  };
  
  return date.toLocaleDateString('en-US', { ...defaultOptions, ...options });
}

/**
 * Format a date with relative terms (Today, Tomorrow, or short date)
 * 
 * @param {string|Date|null} value - Date to format
 * @returns {string|null} - "Today", "Tomorrow", or short date like "Jan 15"
 */
export function formatDateRelative(value) {
  const date = parseDate(value);
  if (!date) return null;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  if (date.getTime() === today.getTime()) return 'Today';
  if (date.getTime() === tomorrow.getTime()) return 'Tomorrow';
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Format a date for month display (e.g., "January 2026")
 * 
 * @param {string|Date|null} value - Date to format
 * @returns {string|null} - Month and year string
 */
export function formatMonth(value) {
  const date = parseDate(value);
  if (!date) return null;
  
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/**
 * Calculate the number of days between two dates.
 * Positive if target is in the future, negative if in the past.
 * 
 * @param {string|Date|null} target - The target date
 * @param {string|Date|null} from - The reference date (defaults to today)
 * @returns {number|null} - Number of days, or null if invalid
 */
export function daysBetween(target, from = new Date()) {
  const targetDate = parseDate(target);
  const fromDate = parseDate(from) || new Date();
  
  if (!targetDate) return null;
  
  fromDate.setHours(0, 0, 0, 0);
  
  const diffMs = targetDate.getTime() - fromDate.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

/**
 * Check if a date is in the past (before today)
 * 
 * @param {string|Date|null} value - Date to check
 * @returns {boolean} - True if date is before today
 */
export function isPast(value) {
  const days = daysBetween(value);
  return days !== null && days < 0;
}

/**
 * Check if a date is today
 * 
 * @param {string|Date|null} value - Date to check
 * @returns {boolean} - True if date is today
 */
export function isToday(value) {
  const days = daysBetween(value);
  return days === 0;
}

/**
 * Check if a date is within a given number of days from today
 * 
 * @param {string|Date|null} value - Date to check
 * @param {number} days - Number of days
 * @returns {boolean} - True if date is within the range
 */
export function isWithinDays(value, days) {
  const diff = daysBetween(value);
  return diff !== null && diff >= 0 && diff <= days;
}

/**
 * Get today's date as a YYYY-MM-DD string
 * 
 * @returns {string} - Today's date
 */
export function today() {
  return toDateString(new Date());
}

/**
 * Add days to a date
 * 
 * @param {string|Date|null} value - Starting date
 * @param {number} days - Days to add (can be negative)
 * @returns {Date|null} - New date, or null if invalid
 */
export function addDays(value, days) {
  const date = parseDate(value);
  if (!date) return null;
  
  date.setDate(date.getDate() + days);
  return date;
}

/**
 * Add months to a date
 * 
 * @param {string|Date|null} value - Starting date
 * @param {number} months - Months to add (can be negative)
 * @returns {Date|null} - New date, or null if invalid
 */
export function addMonths(value, months) {
  const date = parseDate(value);
  if (!date) return null;
  
  date.setMonth(date.getMonth() + months);
  return date;
}

/**
 * Get the start of the current week (Sunday)
 * 
 * @returns {Date} - Start of week
 */
export function startOfWeek() {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - date.getDay());
  return date;
}

/**
 * Get the end of the current week (Saturday)
 * 
 * @returns {Date} - End of week
 */
export function endOfWeek() {
  const date = startOfWeek();
  date.setDate(date.getDate() + 6);
  return date;
}

/**
 * Get the start of the current month
 * 
 * @returns {Date} - Start of month
 */
export function startOfMonth() {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(1);
  return date;
}

/**
 * Get the end of the current month
 * 
 * @returns {Date} - End of month
 */
export function endOfMonth() {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setMonth(date.getMonth() + 1, 0);
  return date;
}
